<script setup lang="ts">
import { Ref, computed, inject, ref } from 'vue';

import {
  TSBadge,
  TSButton,
  TSDataTable,
  TSIcon,
  TSSteps,
  formatDate,
  useLoadingStore,
  useToast,
} from 'tsv2-library';
import {
  TableCellComponent,
  TableColumn,
} from 'tsv2-library/dist/src/components/v2/DataTable/DataTable.vue';
import { Step } from 'tsv2-library/dist/src/components/v2/Steps/Steps.vue';

import ApprovalList from './ApprovalList.vue';
import CopyButton from './CopyButton.vue';
import EditEmail from './EditEmail.vue';
import TimeoutCountdown from './TimeoutCountdown.vue';

import BorrowingServices from '@/services/borrowing.service';

import { ApprovalTransaction } from '@/types/approval.type';
import { BorrowingTransactionDetail } from '@/types/borrowing.type';

const toast = useToast();
const { setLoading } = useLoadingStore();

defineProps<{
  type?: 'Handover' | 'Detail';
  currentStep?: number;
}>();

const transactionId = inject('transactionId') as Ref<string>;
const transactionDetail = inject(
  'transactionDetail',
) as Ref<BorrowingTransactionDetail>;
const approvalList = inject('approvalList') as Ref<ApprovalTransaction[]>;
const getTransactionDetail = inject(
  'getTransactionDetail',
) as () => Promise<void>;

const showApprovalList = ref<boolean>(false);

const statusSeverity = computed(() => {
  if (!transactionDetail.value) return;

  switch (transactionDetail.value.status) {
    case 'Waiting for Approval':
    case 'Waiting for Handover':
    case 'Extend Approval':
    case 'Request Extension':
      return 'warning';
    case 'Rejected':
      return 'danger';
    case 'Returned':
      return 'success';
    default:
      return 'primary';
  }
});

const steps: Step[] = [
  { label: 'Asset(s) Verification' },
  {
    label: 'Confirmation',
  },
];

/**
 * Handle the send email button.
 */
const handleSendEmail = async (): Promise<void> => {
  try {
    setLoading(true);

    await BorrowingServices.postSendConfirmationEmail(transactionId.value);

    await getTransactionDetail();

    toast.add({
      message: 'Success, confirmation email has been sent.',
      severity: 'success',
    });
  } catch (error) {
    toast.add({
      error,
      message: 'Error, failed to send confirmation email.',
      severity: 'error',
    });
  } finally {
    setLoading(false);
  }
};

const columns: TableColumn[] = [
  {
    header: 'Status',
    field: 'confirmationEmailConfirmed',
    reorderable: false,
    sortable: false,
    bodyComponent(data: BorrowingTransactionDetail): TableCellComponent {
      return {
        component: TSBadge,
        props: {
          label: data.confirmationEmailConfirmed
            ? 'Confirmed'
            : 'Waiting for Confirmation',
          format: 'nowrap',
          severity: data.confirmationEmailConfirmed ? 'success' : 'warning',
        },
      };
    },
  },
  {
    header: 'Email',
    field: 'confirmationEmail',
    reorderable: false,
    sortable: false,
    bodyComponent(data: BorrowingTransactionDetail): TableCellComponent {
      return {
        component: EditEmail,
        props: {
          name: data.borrower.fullName,
          email: data.confirmationEmail,
        },
      };
    },
  },
  {
    header: 'Email Status',
    field: 'confirmationEmailSentAt',
    reorderable: false,
    sortable: false,
    bodyTemplate(data: BorrowingTransactionDetail): string {
      return data.confirmationEmailSentAt
        ? `Email sent (${formatDate(data.confirmationEmailSentAt, true)})`
        : 'Email has not been sent';
    },
  },
  {
    header: 'Confirmation Timeout',
    field: 'confirmationEmailExpiredAt',
    reorderable: false,
    sortable: false,
    bodyComponent(data: BorrowingTransactionDetail): TableCellComponent {
      return {
        component: TimeoutCountdown,
        props: {
          data,
        },
      };
    },
  },
  {
    header: 'Action',
    field: 'isVerified',
    reorderable: false,
    sortable: false,
    bodyComponent(data: BorrowingTransactionDetail): TableCellComponent {
      return {
        component: TSButton,
        props: {
          label: data.confirmationEmailSentAt ? 'Resend Email' : 'Send Email',
          onClick: handleSendEmail,
          disabled: data.confirmationEmailConfirmed,
        },
      };
    },
  },
];
</script>
<template>
  <div
    v-if="transactionDetail"
    class="flex flex-col gap-3"
    data-ts-name="transaction-detail"
  >
    <div
      class="text-general-800 tracking-[0.24px] flex justify-between items-center"
      data-ts-section="transaction-detail-header"
    >
      <div class="flex gap-2 items-center">
        <span
          class="text-sm font-bold leading-[18px]"
          data-ts-section="transaction-id"
        >
          {{ transactionDetail.transactionId }}
        </span>
        <CopyButton :value="transactionDetail.transactionId" />
        <TSBadge
          :label="transactionDetail.status"
          :severity="statusSeverity"
          format="nowrap"
        />
      </div>
      <div v-if="type === 'Handover'">
        <TSSteps :current-step :steps />
      </div>
      <div class="text-xs font-medium leading-[normal]">
        <span>Last Update: </span>
        <span>{{ formatDate(transactionDetail.updatedAt, true) }}</span>
      </div>
    </div>
    <div
      class="text-xs text-general-800 font-medium tracking-[0.24px] leading-[normal] flex justify-between items-center"
    >
      <div class="flex gap-3 items-center">
        <div class="flex items-center gap-[2px]">
          <span>Group:</span>
          <TSBadge
            :label="transactionDetail.group.name"
            format="nowrap"
            severity="primary"
          />
        </div>
        <div class="flex items-center gap-[2px]">
          <span>Manager:</span>
          <TSIcon class="h-4 w-4 text-general-400" icon="user" />
          <span class="text-primary-500">
            {{ transactionDetail.manager.fullName }}
          </span>
        </div>
        <div
          v-if="type === 'Handover' || type === 'Detail'"
          class="flex items-center gap-[2px]"
        >
          <span>Borrower:</span>
          <TSIcon class="h-4 w-4 text-general-400" icon="user" />
          <span class="text-primary-500">
            {{ transactionDetail.borrower.fullName }}
          </span>
        </div>
      </div>
      <div v-if="approvalList" class="flex items-center gap-[2px]">
        <span>Approver: </span>
        <ApprovalList
          v-if="approvalList.length"
          v-model:visible="showApprovalList"
          :approvals="approvalList"
        />
        <span v-else>-</span>
      </div>
    </div>
    <div
      v-if="currentStep === 2"
      data-ts-section="transaction-detail-confirmation"
    >
      <TSDataTable
        :columns
        :custom-column="false"
        :data="[transactionDetail]"
        :use-option="false"
        data-key="_id"
        selection-type="single"
      />
    </div>
  </div>
</template>
